import { HttpClient } from '@angular/common/http';
import * as _ from 'underscore';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { StorageSessionService } from '../../../core/storage/services/storage.session.service';

import {
	Configuration,
	NavigateProcessRequestStatus,
	NAVIGATION_REQUEST_STATUSES
} from '../../analytics-core/interfaces/configuration.interface';
import { Partner } from '../../analytics-core/interfaces/partner.interface';
import { QueryParameters } from '../../analytics-core/interfaces/query-parameters.interface';
import { Widget } from '../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../analytics-core/services/color.utility.service';
import { ConfigurationMapperService } from '../../analytics-core/services/configuration-mapper.service';
import {
	ANALYTICS_CONFIGS,
	OPTIONS,
	PRINTABLE_ACCESS_KEYS,
	PRISTINE_PERMISSIONS
} from '../constants/analytics.constants';
import { Advertiser } from '../interfaces/advertiser.interface';
import { Campaign } from '../interfaces/campaign.interface';
import { SetupData } from '../interfaces/setupdata.interface';
import { User } from '../interfaces/user.interface';
import { Mixpanel } from '../interfaces/mixpanel.interface';
import { AnalyticsMapperService } from '../services/analytics.mapper.service';

/**
 *
 */
@Injectable()
export class AnalyticsDataModel {
	/**
	 *
	 * @returns {Configuration}
	 */
	get config(): Configuration {
		return this._config;
	}

	/**
	 *
	 * @param value
	 */
	set config(value: Configuration) {
		this._config = value;
	}

	get setupData(): SetupData {
		return this._setupData;
	}

	set setupData(value: SetupData) {
		this._setupData = value;
	}

	get user(): User {
		return this._user;
	}

	set user(value: User) {
		this._user = value;
	}

	set goLiveDate(value: Date) {
		this._goLiveDate = value;
	}

	get goLiveDate(): Date {
		return this._goLiveDate;
	}

	set depricatedDate(value: Date) {
		this._depricatedDate = value;
	}

	get depricatedDate(): Date {
		return this._depricatedDate;
	}

	set partner(value: Partner) {
		this._partner = value;
	}

	get partner(): Partner {
		return this._partner;
	}

	get advertisers(): Advertiser[] {
		return this._advertisers;
	}

	set advertisers(value: Advertiser[]) {
		this._advertisers = value;
	}

	get campaigns(): Campaign[] {
		return this._campaigns;
	}

	set campaigns(value: Campaign[]) {
		this._campaigns = value;
	}

	get myCampaigns(): any[] {
		return this._campaigns;
	}

	set myCampaigns(value: any[]) {
		this._campaigns = value;
	}

	get startDate(): any {
		return this._startDate;
	}

	set startDate(value: any) {
		this._startDate = value;
	}

	get endDate(): any {
		return this._endDate;
	}

	set endDate(value: any) {
		this._endDate = value;
	}

	get selectedAdvertisers(): Advertiser[] {
		return this._selectedAdvertisers;
	}

	set selectedAdvertisers(value: Advertiser[]) {
		this._selectedAdvertisers = value;
	}

	get selectedCampaigns(): Campaign[] {
		return this._selectedCampaigns;
	}

	set selectedCampaigns(value: Campaign[]) {
		this._selectedCampaigns = value;
	}

	get selectedActiveCampaigns(): Campaign[] {
		return this._selectedActiveCampaigns;
	}

	set selectedActiveCampaigns(value: Campaign[]) {
		this._selectedActiveCampaigns = value;
	}

	get hasData(): boolean {
		return this._hasData;
	}

	set hasData(value: boolean) {
		this._hasData = value;
	}

	get navigateProcessRequestStatus(): NavigateProcessRequestStatus {
		return this._navigateProcessRequestStatus;
	}

	set navigateProcessRequestStatus(value: NavigateProcessRequestStatus) {
		this._navigateProcessRequestStatus = value;
	}

	get queryParamsCampaigns(): string[] {
		return this._queryparamsCampaigns;
	}

	set queryParamsCampaigns(value: string[]) {
		this._queryparamsCampaigns = value;
	}

	get mixpanel(): Mixpanel {
		return this._mixpanel;
	}

	set mixpanel(value: Mixpanel) {
		this._mixpanel = value;
	}

	/**
	 *
	 * @param response
	 */
	set data(setupResponse: any) {
		this.setupData = this.analyticsMapperService.mapSetupDataToInterface(setupResponse);
		this.user = this.setupData.user;
		this.mixpanel = this.setupData.mixpanel;
		this.partner = this.setupData.partner;
		this.depricatedDate = this.setupData.depricatedDate;
		this.goLiveDate = this.setupData.goLiveDate;
		this.advertisers = this.setupData.advertisers;
		this.depricatedDate = this.setupData.depricatedDate;
		this.goLiveDate = this.setupData.goLiveDate;
		this.colorUtilityService.buildStylesFromColorPalette(this.partner.colorPalette);
		this.colorUtilityService.buildStylesFromColorPaletteTwo(this.partner.colorPalette);
	}

	set configData(configResponse: any) {
		if (this.config.partner.filterAdvertisers) {
			this.filterAdvertisers();
		}
		this.preSelectAdvertisers(OPTIONS.ADVERTISERS.DEFAULT_SELECTION);
		this.filterWidgets();
		this.colorUtilityService.loadStyleSheets(this.config.styles);
		this.setFavIcon();
		this.setDefaultNavigateProcessRequestStatuses();
	}

	set campaignsData(campaignResponse: any[]) {
		this.campaigns = this.analyticsMapperService.mapCampaignsDataToInterface(campaignResponse);
		this.preselectCampaigns();
	}

	set campaignsDataWithoutPreselect(campaignResponse: any[]) {
		this.campaigns = this.analyticsMapperService.mapCampaignsDataToInterface(campaignResponse);
	}

	get advertisersListForDropDown(): any[] {
		return this.analyticsMapperService.mapAdvertisersFromDropdown(this.advertisers);
	}

	get selectedAdvertisersListForDropDown(): any[] {
		return this.analyticsMapperService.mapSelectedAdvertisersFromDropdown(this.advertisers);
	}

	get campaignsListForDropDown(): any[] {
		return this.campaigns ? this.analyticsMapperService.mapCampaignsFromDropdown(this.campaigns) : [];
	}

	get selectedCampaignsListForDropDown(): any[] {
		let selectedCampaigns = this.campaigns
			? this.analyticsMapperService.mapSelectedCampaignsFromDropdown(this.campaigns)
			: [];
		this.selectedActiveCampaigns = selectedCampaigns.filter((campaign) => campaign.active);
		return selectedCampaigns;
	}

	get requestObjectForProcessRequest(): any {
		let queryParams = this.getQueryParamsObj();
		this.startDate = this.startDate
			? this.startDate
			: queryParams['start_date'] !== ''
			? queryParams['start_date']
			: undefined;
		this.endDate = this.endDate ? this.endDate : queryParams['end_date'] !== '' ? queryParams['end_date'] : undefined;
		this.selectedActiveCampaigns =
			this.selectedActiveCampaigns.length > 0
				? this.selectedActiveCampaigns
				: this.selectedCampaigns.filter((campaign) => campaign.active);
		if (this.selectedAdvertisers.length > 0 && this.selectedActiveCampaigns.length > 0) {
			return this.analyticsMapperService.mapInterfaceToRequestObject(
				this.selectedAdvertisers,
				this.selectedActiveCampaigns,
				this.startDate,
				this.endDate
			);
		} else {
			return this.analyticsMapperService.mapInterfaceToRequestObject([], [], this.startDate, this.endDate);
		}
	}

	set printableReport(value: any) {
		this._printableReport = value;
	}

	get printableReport(): any {
		return this._printableReport;
	}

	set overViewReportDetails(value: any) {
		this._overViewReportDetails = value;
	}

	get overViewReportDetails(): any {
		return this._overViewReportDetails;
	}

	set userInteracted(value: boolean) {
		this._userInteracted = value;
	}

	get userInteracted(): boolean {
		return this._userInteracted;
	}
	/**
	 *
	 */
	private _config: Configuration;
	/**
	 *
	 */
	private _setupData: SetupData;
	/**
	 *
	 */
	private _user: User;
	/**
	 *
	 */
	private _partner: Partner;
	/**
	 *
	 */
	private _advertisers: Advertiser[];
	/**
	 *
	 */
	private _campaigns: Campaign[];
	/**
	 *
	 */
	private _startDate: any;
	/**
	 *
	 */
	private _endDate: any;
	/**
	 *
	 */
	private _selectedAdvertisers: Advertiser[] = [];
	/**
	 *
	 */
	private _selectedCampaigns: Campaign[] = [];
	/**
	 *
	 */
	private _selectedActiveCampaigns: Campaign[] = [];
	/**
	 *
	 */
	private _currentPath: string;
	/**
	 *
	 */
	private _hasData: boolean;

	/**
	 *
	 */
	private _goLiveDate: Date;
	/**
	 *
	 */
	private _depricatedDate: Date;
	/**
	 *
	 */
	private _printableReport: any;

	/**
	 *
	 */
	private _overViewReportDetails: any;

	/**
	 *
	 */
	private _navigateProcessRequestStatus: NavigateProcessRequestStatus;

	/**
	 *
	 */
	private _queryparamsCampaigns: string[] = [];

	/**
	 *
	 */
	private _userInteracted: boolean = false;

	private _mixpanel: any;
	public productCampaignsActiveCount: any = 0;

	/**
	 *
	 */
	constructor(
		protected http: HttpClient,
		private configurationMapperService: ConfigurationMapperService,
		private colorUtilityService: ColorUtilityService,
		private analyticsMapperService: AnalyticsMapperService,
		private router: Router,
		private storageSessionService: StorageSessionService
	) {}

	setFavIcon(): void {
		const link = document.createElement('link');
		const oldLink = document.getElementById('favicon');
		const favIcon = this.setupData.partner.favIcon;

		link.id = 'dynamic-favicon';
		link.rel = 'shortcut icon';
		link.href = `${favIcon}?=${Math.random()}`;

		if (oldLink) {
			document.head.removeChild(oldLink);
		}
		document.head.appendChild(link);
	}

	preSelectAdvertisers(count: any): void {
		const queryParamsObj = this.getQueryParamsObj();
		if (queryParamsObj.advertiser_ids && queryParamsObj.advertiser_ids !== '') {
			const queryParamsAdvIdsArray = queryParamsObj.advertiser_ids.includes(',')
				? queryParamsObj.advertiser_ids.split(',')
				: [].concat(queryParamsObj.advertiser_ids);
			const selctedAdvIds = [];
			this.advertisers.forEach((adv) => {
				if (queryParamsAdvIdsArray.includes(adv.id)) {
					selctedAdvIds.push(adv.id);
					adv.selected = true;
				} else {
					adv.selected = false;
				}
			});
		} else if (JSON.stringify(queryParamsObj) === '{}') {
			const demoAdvertisers = _.where(this.advertisers, { isDemoAdvertiser: true });
			demoAdvertisers.length > 0
				? this.selectAdvertisersByCount(demoAdvertisers, count, false)
				: this.selectAdvertisersByCount(this.advertisers, count, true);
		}
		this.selectedAdvertisers = _.where(this.advertisers, { selected: true });
	}

	preselectCampaigns(): void {
		const queryParamsObj = this.getQueryParamsObj();
		if (!this.userInteracted && JSON.stringify(queryParamsObj) !== '{}') {
			if (this.queryParamsCampaigns.length) {
				this.campaigns.forEach((cmp) => {
					cmp.selected = this.queryParamsCampaigns.includes(cmp.campaignId) ? true : false;
				});
			} else if (queryParamsObj.campaign_ids && queryParamsObj.campaign_ids !== '') {
				const queryParamsCmpIdsArray = queryParamsObj.campaign_ids.includes(',')
					? queryParamsObj.campaign_ids.split(',')
					: [].concat(queryParamsObj.campaign_ids);
				this.campaigns.forEach((cmp) => {
					cmp.selected = queryParamsCmpIdsArray.includes(cmp.campaignId) ? true : false;
				});
				const selectedCampaigns = this.campaigns.filter((obj) => obj.selected);
				this.queryParamsCampaigns = selectedCampaigns.map((obj) => obj.campaignId);
			} else {
				this.campaigns.forEach((cmp) => {
					cmp.selected = false;
				});
			}
			// adding condition like queryParams === "?product=overview"  , to set campaigns as selected in campaigns dropdown
			// when the user is logged in through SSO
		} else if (
			this.storageSessionService.queryParams === '' ||
			this.storageSessionService.queryParams === '?product=overview'
		) {
			_.map(this.campaigns, (cmp: any) => {
				cmp.selected = true;
			});
		}
	}

	selectAdvertisersByCount(advertisers: Advertiser[], count: number, applyCondition?: boolean): void {
		if (applyCondition ? this.advertisers.length <= count : true) {
			_.map(advertisers, (advertiser, index) => {
				if (index < count) {
					advertiser.selected = true;
				}
			});
		}
	}

	getSelectedAdvertiserIds(): any[] {
		const advertiserIds = _.pluck(this.selectedAdvertisersListForDropDown, 'id');
		return advertiserIds;
	}

	getCurrentPath(): string {
		const pathWithoutQueryParams = this.router.url.split('?')[0];
		const path: string[] = pathWithoutQueryParams.split('/');
		const currentURL = path.length > 1 ? path[2] : path[1];
		return currentURL;
	}

	setCurrentPath(path: string): void {
		this._currentPath = path;
	}

	getQueryParamsObj(): QueryParameters {
		const queryParamsObj: QueryParameters = {};
		if (window.location.search !== '') {
			const queryParamsString = window.location.search.split('?')[1];
			const queryParamsProperties = queryParamsString.split('&');
			queryParamsProperties.forEach((obj) => {
				queryParamsObj[obj.split('=')[0]] = obj.split('=')[1];
			});
		}
		return queryParamsObj;
	}

	public checkComponentForPermission(widget: any): boolean {
		if (!widget.permission && !widget.permissionWithBooleanCheck) {
			return true;
		}
		if (widget.permission) {
			const widgetPermissions = widget.permission.flags;
			for (const permission of widgetPermissions) {
				if (_.indexOf(this.setupData.permissions, permission) !== -1) {
					return true;
				}
			}
		}
		if (widget.permissionWithBooleanCheck) {
			let allPermissionsGranted = true;
			for (const permission of widget.permissionWithBooleanCheck) {
				const permissionAllowed =
					_.indexOf(this.setupData.permissions, permission.flag) !== -1 ? permission.flagCheck : !permission.flagCheck;
				allPermissionsGranted = allPermissionsGranted ? permissionAllowed : allPermissionsGranted;
			}
			return allPermissionsGranted;
		}
		return false;
	}

	public checkOverviewPrintablePermission(permission) {
		if (_.indexOf(this.setupData.permissions, permission) !== -1) {
			return true;
		}
	}

	setFinalConfig(partnerConfigResponse: any, defaultConfigResponse: any = false): any {
		const partnerConfig = this.configurationMapperService.mapConfigurationResponseToInterface(partnerConfigResponse);
		if (!defaultConfigResponse) {
			this.config = partnerConfig;
		} else {
			const defaulfConfig = this.configurationMapperService.mapConfigurationResponseToInterface(defaultConfigResponse);
			this.config = this.configurationMapperService.mergeParentChildConfig(defaulfConfig, partnerConfig);
		}
		this.configData = this.config;
	}

	private filterAdvertisers(): void {
		this.advertisers = this.advertisers.filter((advertiser) => {
			return advertiser.isTTDEnabled;
		});
	}

	private filterWidgets(): void {
		this.config.navWidgets = _.filter(this.config.navWidgets, (widget) => {
			return this.checkIfWidgetHasPermissionToLoad(widget);
		});
		this.config.widgets = _.filter(this.config.widgets, (widget) => {
			return this.checkIfWidgetHasPermissionToLoad(widget);
		});
		for (const product of this.config.products) {
			product.widgets = _.filter(product.widgets, (widget) => {
				return this.checkIfWidgetHasPermissionToLoad(widget);
			});
			product.overviewWidgets = _.filter(product.overviewWidgets, (widget) => {
				return this.checkIfWidgetHasPermissionToLoad(widget);
			});
		}
	}

	/**
	 *
	 * @param {Widget} widget
	 * @returns {boolean}
	 */
	private checkIfWidgetHasPermissionToLoad(widget: Widget): boolean {
		if (widget.permission.isGeneric) {
			return true;
		} else if (widget.widgetType === 'nav-link' && widget.permissionWithBooleanCheck[0].flagCheck) {
			let allPermissionsGranted = true;
			let permissionWithBooleanChekObj: any = widget.permissionWithBooleanCheck;
			for (const permission of permissionWithBooleanChekObj) {
				const permissionAllowed =
					_.indexOf(this.setupData.permissions, permission.flags) !== -1 ? permission.flagCheck : !permission.flagCheck;
				allPermissionsGranted = allPermissionsGranted ? permissionAllowed : allPermissionsGranted;
			}
			return allPermissionsGranted;
		} else if (widget.permissionWithBooleanCheck && widget.permissionWithBooleanCheck[0].flags.length > 0) {
			let allPermissionsGranted = true;
			let permissionWithBooleanChekObj: any = widget.permissionWithBooleanCheck;
			for (const permission of permissionWithBooleanChekObj) {
				const permissionAllowed =
					_.indexOf(this.setupData.permissions, permission.flags) !== -1 ? permission.flagCheck : !permission.flagCheck;
				allPermissionsGranted = allPermissionsGranted ? permissionAllowed : allPermissionsGranted;
			}
			return allPermissionsGranted;
		} else {
			const widgetPermissions = widget.permission.flags;
			for (const permission of widgetPermissions) {
				if (_.indexOf(this.setupData.permissions, permission) !== -1) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * This property remembers the status of processRequest call for each Navigation widgets
	 */
	private setDefaultNavigateProcessRequestStatuses(): void {
		const accessibleNavWidgets = this.config.navWidgets;
		const result: NavigateProcessRequestStatus = {} as NavigateProcessRequestStatus;
		for (const navWidget of accessibleNavWidgets) {
			result[navWidget.widgetPath] = NAVIGATION_REQUEST_STATUSES.IN_PROGRESS;
		}
		this.navigateProcessRequestStatus = result;
	}
}
